/* eslint-disable max-lines */
import isNil from "lodash/isNil";
import { v4 as uuid } from "uuid";

import {
  MetricRangeType,
  type MetricRange,
  type UpsertMetricRangeInput,
} from "src/libs/waterly-graphql-types/src";

import { SeverityColor } from "../SeveritySelect/severity.utils";

export type LimitRangeSide = MetricRangeType.Lower | MetricRangeType.Upper;

export type MetricRangeCardMetricRange = Omit<UpsertMetricRangeInput, "metricId" | "id"> & {
  id: string;
  //Used for deleting ranges in the editor
  deletedAt?: string | null;
  //used to determine if a range should be removed or simply removed from the frontend before saving
  isNew?: boolean;
};

/** Creates a new warning range with editor defaults and a generated client-side ID. */
export const createWarningRange = ({
  rangeMinInclusive,
  rangeMaxInclusive,
  rangeMin,
  rangeMax,
  name,
  color = SeverityColor.YELLOW,
}: Pick<
  MetricRangeCardMetricRange,
  "rangeMinInclusive" | "rangeMaxInclusive" | "rangeMin" | "rangeMax" | "name"
> & {
  color?: string;
}): MetricRangeCardMetricRange => ({
  id: uuid(),
  color,
  deletedAt: undefined,
  ideal: false,
  notificationEnabled: false,
  isNew: true,
  rangeMax,
  rangeMin,
  rangeMaxInclusive,
  rangeMinInclusive,
  name,
});

/** Builds a stable React key for a metric range, falling back to its identifying range values. */
export const metricRangeCardMetricRandKeyGenerator = (range: MetricRangeCardMetricRange) => {
  return range.id ?? `${range.name}-${range.rangeMin ?? "min"}-${range.rangeMax ?? "max"}-${range.ideal}`;
};

export type BuildRangeExpressionInput = Pick<
  MetricRange,
  "rangeMin" | "rangeMinInclusive" | "rangeMax" | "rangeMaxInclusive"
> & {
  invalidRangeString?: string;
  removeParentheses?: boolean;
};

export enum InequalityOperator {
  GREATER_THAN = "GREATER_THAN",
  LESS_THAN = "LESS_THAN",
}

export type MetricRangeFormikValues = {
  id?: string;
  name: string;
  rangeMin: number | null;
  rangeMax: number | null;
  rangeMinInclusive: boolean;
  rangeMaxInclusive: boolean;
  notificationEnabled: boolean;
  color: string;
  minTagId: string | null;
  maxTagId: string | null;
  ideal: boolean;
};

//A singleton range covers exactly one value (x = rangeMin = rangeMax). Pulling a neighboring
//boundary onto a singleton would leave it empty, while collapsing a wider range into a
//singleton is allowed.
export const isSingletonRange = (range?: Pick<MetricRange, "rangeMin" | "rangeMax">) =>
  !isNil(range?.rangeMin) && range.rangeMin === range.rangeMax;

/** Returns the inequality symbol for the requested direction and inclusivity. */
export const getInequalitySymbol = (operatorType: InequalityOperator, inclusive: boolean) => {
  if (operatorType === InequalityOperator.GREATER_THAN) return inclusive ? "\u2265" : ">";
  return inclusive ? "\u2264" : "<";
};

/** Formats metric range bounds as a human-readable inequality expression. */
export const buildRangeExpression = ({
  rangeMin,
  rangeMax,
  rangeMinInclusive,
  rangeMaxInclusive,
  invalidRangeString = "-∞ < x < ∞",
  removeParentheses = false,
}: BuildRangeExpressionInput) => {
  const rangeMinAndRangeMaxAreBothSet = !isNil(rangeMin) && !isNil(rangeMax);
  const onlyRangeMaxIsSet = isNil(rangeMin) && !isNil(rangeMax);
  const onlyRangeMinIsSet = !isNil(rangeMin) && isNil(rangeMax);

  const rangeMinOperator = getInequalitySymbol(InequalityOperator.LESS_THAN, rangeMinInclusive);
  const rangeMaxOperator = getInequalitySymbol(InequalityOperator.LESS_THAN, rangeMaxInclusive);
  const alternativeRangeMinOperator = getInequalitySymbol(InequalityOperator.GREATER_THAN, rangeMinInclusive);

  let expression = invalidRangeString;
  if (rangeMinAndRangeMaxAreBothSet) {
    if (rangeMin === rangeMax) {
      expression = `x = ${rangeMin}`;
    } else {
      expression = `${rangeMin} ${rangeMinOperator} x ${rangeMaxOperator} ${rangeMax}`;
    }
  } else if (onlyRangeMaxIsSet) {
    expression = `x ${rangeMaxOperator} ${rangeMax}`;
  } else if (onlyRangeMinIsSet) {
    expression = `x ${alternativeRangeMinOperator} ${rangeMin}`;
  }

  return removeParentheses ? expression : `(${expression})`;
};

/** Converts an editor metric range into normalized Formik values. */
export const sanitizeMetricRange = (metricRange: MetricRangeCardMetricRange): MetricRangeFormikValues => {
  return {
    id: metricRange.id ?? undefined,
    name: metricRange.name,
    rangeMin: metricRange.rangeMin ?? null,
    rangeMax: metricRange.rangeMax ?? null,
    notificationEnabled: metricRange.notificationEnabled,
    rangeMinInclusive: metricRange.rangeMinInclusive,
    rangeMaxInclusive: metricRange.rangeMaxInclusive,
    color: metricRange.color,
    minTagId: metricRange.minTagId ?? null,
    maxTagId: metricRange.maxTagId ?? null,
    ideal: metricRange.ideal,
  };
};

/** Normalizes shared-boundary inclusivity so ideal and singleton ranges own their boundary values. */
export const normalizeMetricRangeInclusivity = (
  metricRanges: MetricRangeCardMetricRange[],
): MetricRangeCardMetricRange[] => {
  const specialRanges = metricRanges.filter(
    (range) => range.ideal || (range.rangeMin !== null && range.rangeMin === range.rangeMax),
  );

  return metricRanges.map((range) => {
    if (specialRanges.some((specialRange) => specialRange.id === range.id)) {
      return { ...range, rangeMinInclusive: true, rangeMaxInclusive: true };
    }

    return {
      ...range,
      rangeMinInclusive: !specialRanges.some(
        (specialRange) => specialRange.rangeMax !== null && specialRange.rangeMax === range.rangeMin,
      )
        ? range.rangeMinInclusive
        : false,
      rangeMaxInclusive: !specialRanges.some(
        (specialRange) => specialRange.rangeMin !== null && specialRange.rangeMin === range.rangeMax,
      )
        ? range.rangeMaxInclusive
        : false,
    };
  });
};

/** Clears duplicate tags from singleton ranges and their adjacent ranges while retaining the selected tag. */
export const normalizeSingletonMetricRangeTags = ({
  metricRanges,
  retainedTags,
}: {
  metricRanges: MetricRangeCardMetricRange[];
  retainedTags: [string | undefined, "minTagId" | "maxTagId"][];
}): MetricRangeCardMetricRange[] =>
  retainedTags.reduce((updatedMetricRanges, [singletonRangeId, retainedTagKey]) => {
    const singletonRange = updatedMetricRanges.find((range) => range.id === singletonRangeId);
    if (
      !singletonRange ||
      singletonRange.rangeMin === null ||
      singletonRange.rangeMin !== singletonRange.rangeMax ||
      !singletonRange.minTagId ||
      !singletonRange.maxTagId
    ) {
      return updatedMetricRanges;
    }

    const clearedTagKey = retainedTagKey === "minTagId" ? "maxTagId" : "minTagId";
    const adjacentTagKey = clearedTagKey === "minTagId" ? "maxTagId" : "minTagId";
    const boundaryKey = clearedTagKey === "minTagId" ? "rangeMax" : "rangeMin";

    return updatedMetricRanges.map((range) => {
      if (range.id === singletonRange.id) return { ...range, [clearedTagKey]: null };

      if (
        range[boundaryKey] === singletonRange.rangeMin &&
        range[adjacentTagKey] === singletonRange[clearedTagKey]
      ) {
        return { ...range, [adjacentTagKey]: null };
      }

      return range;
    });
  }, metricRanges);

/** Keeps both ranges adjacent to an ideal singleton on the same boundary tag. */
export const synchronizeIdealSingletonAdjacentTags = ({
  metricRanges,
  idealRangeId,
  lowerAdjacentRangeId,
  upperAdjacentRangeId,
  retainedIdealTagKey,
  tagId,
}: {
  metricRanges: MetricRangeCardMetricRange[];
  idealRangeId: string | undefined;
  lowerAdjacentRangeId: string | undefined;
  upperAdjacentRangeId: string | undefined;
  retainedIdealTagKey: "minTagId" | "maxTagId";
  tagId: string | null | undefined;
}): MetricRangeCardMetricRange[] => {
  const idealRange = metricRanges.find((range) => range.id === idealRangeId);
  if (!idealRange?.ideal || idealRange.rangeMin === null || idealRange.rangeMin !== idealRange.rangeMax) {
    return metricRanges;
  }

  return metricRanges.map((range) => {
    if (range.id === idealRange.id) {
      const clearedIdealTagKey = retainedIdealTagKey === "minTagId" ? "maxTagId" : "minTagId";
      return { ...range, [retainedIdealTagKey]: tagId, [clearedIdealTagKey]: null };
    }
    if (range.id === lowerAdjacentRangeId) return { ...range, maxTagId: tagId };
    if (range.id === upperAdjacentRangeId) return { ...range, minTagId: tagId };
    return range;
  });
};

/** Keeps an ideal singleton's boundary tag on the unchanged boundary and both adjacent ranges. */
export const synchronizeCollapsedIdealRangeTags = ({
  metricRanges,
  previousIdealRange,
  newIdealRange,
  lowerAdjacentRangeId,
  upperAdjacentRangeId,
}: {
  metricRanges: MetricRangeCardMetricRange[];
  previousIdealRange: MetricRangeCardMetricRange;
  newIdealRange: MetricRangeCardMetricRange;
  lowerAdjacentRangeId: string | undefined;
  upperAdjacentRangeId: string | undefined;
}): MetricRangeCardMetricRange[] => {
  if (
    !newIdealRange.ideal ||
    newIdealRange.rangeMin === null ||
    newIdealRange.rangeMin !== newIdealRange.rangeMax
  ) {
    return metricRanges;
  }

  const retainedIdealTagKey =
    newIdealRange.rangeMin !== previousIdealRange.rangeMin ? "maxTagId" : "minTagId";
  const fallbackIdealTagKey = retainedIdealTagKey === "minTagId" ? "maxTagId" : "minTagId";
  const tagId = newIdealRange[retainedIdealTagKey] ?? newIdealRange[fallbackIdealTagKey] ?? null;

  return synchronizeIdealSingletonAdjacentTags({
    metricRanges,
    idealRangeId: newIdealRange.id,
    lowerAdjacentRangeId,
    upperAdjacentRangeId,
    retainedIdealTagKey,
    tagId,
  });
};

/** Moves a singleton boundary tag to an adjacent range and removes duplicate references to that tag. */
export const moveSingletonMetricRangeTagToAdjacentRange = ({
  metricRanges,
  singletonRangeId,
  sourceTagKey,
  adjacentRangeId,
  adjacentTagKey,
}: {
  metricRanges: MetricRangeCardMetricRange[];
  singletonRangeId: string;
  sourceTagKey: "minTagId" | "maxTagId";
  adjacentRangeId: string | undefined;
  adjacentTagKey: "minTagId" | "maxTagId";
}): MetricRangeCardMetricRange[] => {
  const singletonRange = metricRanges.find((range) => range.id === singletonRangeId);
  const tagId = singletonRange?.[sourceTagKey];
  if (
    !singletonRange ||
    singletonRange.rangeMin === null ||
    singletonRange.rangeMin !== singletonRange.rangeMax ||
    !tagId ||
    !adjacentRangeId
  ) {
    return metricRanges;
  }

  const retainedSingletonTagKey = sourceTagKey === "minTagId" ? "maxTagId" : "minTagId";

  return metricRanges.map((range) => {
    if (range.id === singletonRangeId) {
      return { ...range, [sourceTagKey]: null, [retainedSingletonTagKey]: tagId };
    }
    if (range.id === adjacentRangeId) return { ...range, [adjacentTagKey]: tagId };

    const rangeUpdates: Partial<MetricRangeCardMetricRange> = {};
    if (range.rangeMin === singletonRange.rangeMin && range.minTagId === tagId) rangeUpdates.minTagId = null;
    if (range.rangeMax === singletonRange.rangeMin && range.maxTagId === tagId) rangeUpdates.maxTagId = null;
    return Object.keys(rangeUpdates).length > 0 ? { ...range, ...rangeUpdates } : range;
  });
};

/** Finds the range that should absorb the boundary of a range being deleted from the requested side. */
export const findAdjacentMetricRangeForDeletion = ({
  ranges,
  range,
  metricRangeTypeSide,
}: {
  ranges: MetricRangeCardMetricRange[];
  range: MetricRangeCardMetricRange;
  metricRangeTypeSide: MetricRangeType.Upper | MetricRangeType.Lower;
}) =>
  ranges.find(
    (candidate) =>
      candidate.id !== range.id &&
      (metricRangeTypeSide === MetricRangeType.Lower
        ? candidate.rangeMin === range.rangeMax
        : candidate.rangeMax === range.rangeMin),
  );

/** Sorts metric ranges from lowest to highest bounds, treating null bounds as infinities. */
export const sortMetricRangesByBounds = (
  firstRange: MetricRangeCardMetricRange,
  secondRange: MetricRangeCardMetricRange,
) => {
  const minDifference =
    (firstRange.rangeMin ?? Number.NEGATIVE_INFINITY) - (secondRange.rangeMin ?? Number.NEGATIVE_INFINITY);
  if (minDifference !== 0) return minDifference;

  return (
    (firstRange.rangeMax ?? Number.POSITIVE_INFINITY) - (secondRange.rangeMax ?? Number.POSITIVE_INFINITY)
  );
};

/** Updates an edited range and its bordering range while synchronizing boundaries, tags, and inclusivity. */
export const updateMetricRangesWithBorderingRange = ({
  metricRanges,
  newMetricRange,
  borderingRange,
  oldBoundary,
  newBoundary,
  borderingRangeBoundaryKey,
  newMetricRangeTagKey,
  borderingRangeTagKey,
}: {
  metricRanges: MetricRangeCardMetricRange[];
  newMetricRange: MetricRangeCardMetricRange;
  borderingRange: MetricRangeCardMetricRange | undefined;
  oldBoundary: number | null | undefined;
  newBoundary: number | null | undefined;
  borderingRangeBoundaryKey: "rangeMin" | "rangeMax";
  newMetricRangeTagKey?: "minTagId" | "maxTagId";
  borderingRangeTagKey?: "minTagId" | "maxTagId";
}) => {
  const editedRangeBoundaryKey = borderingRangeBoundaryKey === "rangeMin" ? "rangeMax" : "rangeMin";
  const editedRangeInclusiveKey =
    editedRangeBoundaryKey === "rangeMin" ? "rangeMinInclusive" : "rangeMaxInclusive";
  const borderingRangeInclusiveKey =
    borderingRangeBoundaryKey === "rangeMin" ? "rangeMinInclusive" : "rangeMaxInclusive";
  const newMetricRangeIsSingleton =
    newMetricRange.rangeMin !== null && newMetricRange.rangeMin === newMetricRange.rangeMax;
  const normalizedNewMetricRange = {
    ...newMetricRange,
    ...(newMetricRange.ideal || newMetricRangeIsSingleton
      ? { rangeMinInclusive: true, rangeMaxInclusive: true }
      : { [editedRangeInclusiveKey]: false }),
  };

  const updatedMetricRanges = metricRanges.map((range) => {
    if (range.id === newMetricRange.id) {
      return normalizedNewMetricRange;
    }

    if (range.id === borderingRange?.id && range[borderingRangeBoundaryKey] === oldBoundary) {
      const rangeUpdates: Partial<MetricRangeCardMetricRange> = {};

      if (oldBoundary !== newBoundary) {
        rangeUpdates[borderingRangeBoundaryKey] = newBoundary;
      }

      if (
        newMetricRangeTagKey &&
        borderingRangeTagKey &&
        range[borderingRangeTagKey] !== newMetricRange[newMetricRangeTagKey]
      ) {
        rangeUpdates[borderingRangeTagKey] = newMetricRange[newMetricRangeTagKey];
      }

      if (range.ideal) {
        rangeUpdates.rangeMinInclusive = true;
        rangeUpdates.rangeMaxInclusive = true;
      } else {
        rangeUpdates[borderingRangeInclusiveKey] = !(
          normalizedNewMetricRange.ideal || newMetricRangeIsSingleton
        );
      }

      if (Object.keys(rangeUpdates).length > 0) {
        return { ...range, ...rangeUpdates };
      }
    }

    return range;
  });

  return normalizeMetricRangeInclusivity(updatedMetricRanges);
};

/** Combines updated active ranges with previously deleted ranges that must remain in the save payload. */
export const preserveDeletedMetricRanges = ({
  metricRanges,
  updatedActiveMetricRanges,
}: {
  metricRanges: MetricRangeCardMetricRange[];
  updatedActiveMetricRanges: MetricRangeCardMetricRange[];
}) => [...updatedActiveMetricRanges, ...metricRanges.filter((metricRange) => metricRange.deletedAt)];

/**
 * Splits the outermost range to add a warning range, or anchors it to the ideal range when the side is empty.
 * Returns undefined when no anchor boundary can be determined.
 */
export const buildActiveMetricRangesWithAddedSideRange = ({
  side,
  sideRanges,
  idealRange,
  activeMetricRanges,
  newRangeName,
  newRangeColor,
}: {
  side: MetricRangeType.Lower | MetricRangeType.Upper;
  sideRanges: MetricRangeCardMetricRange[];
  idealRange: MetricRangeCardMetricRange | undefined;
  activeMetricRanges: MetricRangeCardMetricRange[];
  newRangeName: string;
  newRangeColor: string;
}): MetricRangeCardMetricRange[] | undefined => {
  const isLowerSide = side === MetricRangeType.Lower;
  const priorInfinityRange = isLowerSide ? sideRanges[0] : sideRanges[sideRanges.length - 1];

  const getNewProposedBoundaryValue = () => {
    if (!priorInfinityRange) {
      return isLowerSide ? idealRange?.rangeMin : idealRange?.rangeMax;
    }
    return isLowerSide ? (priorInfinityRange.rangeMax ?? 0) - 1 : (priorInfinityRange.rangeMin ?? 0) + 1;
  };

  const newProposedBoundaryValue = getNewProposedBoundaryValue();
  if (isNil(newProposedBoundaryValue)) return undefined;

  const getUpdatedPriorInfinityRange = () => {
    if (!priorInfinityRange) return undefined;
    if (isLowerSide) {
      return { ...priorInfinityRange, rangeMin: newProposedBoundaryValue, rangeMinInclusive: true };
    }
    return { ...priorInfinityRange, rangeMax: newProposedBoundaryValue, rangeMaxInclusive: true };
  };

  const updatedPriorInfinityRange = getUpdatedPriorInfinityRange();

  const newRange = {
    ...createWarningRange({
      name: newRangeName,
      rangeMinInclusive: false,
      rangeMaxInclusive: false,
      //null covers -infinity/infinity
      rangeMin: isLowerSide ? null : newProposedBoundaryValue,
      rangeMax: isLowerSide ? newProposedBoundaryValue : null,
      color: newRangeColor,
    }),
    ...(isLowerSide
      ? { maxTagId: updatedPriorInfinityRange?.minTagId }
      : { minTagId: updatedPriorInfinityRange?.maxTagId }),
  };

  const remainingActiveRanges = activeMetricRanges.filter((range) => range.id !== priorInfinityRange?.id);

  return normalizeMetricRangeInclusivity(
    updatedPriorInfinityRange
      ? [newRange, updatedPriorInfinityRange, ...remainingActiveRanges]
      : [newRange, ...remainingActiveRanges],
  );
};

/** Determines whether regulatory and operational tag controls should be shown for a range position. */
export const getRegulatoryOperationalVisibility = ({
  metricRangeSide,
  index,
  regulatoryIndex,
  operationalIndex,
}: {
  metricRangeSide: MetricRangeType.Upper | MetricRangeType.Lower;
  index: number;
  regulatoryIndex: number;
  operationalIndex: number;
}): { showRegulatory: boolean; showOperational: boolean } => {
  const isSelectedAtCurrentIndex = (selectedIndex: number) => selectedIndex === index;
  const isUnselected = (selectedIndex: number) => selectedIndex === -1;

  if (metricRangeSide === MetricRangeType.Lower) {
    return {
      showRegulatory:
        isSelectedAtCurrentIndex(regulatoryIndex) ||
        (isUnselected(regulatoryIndex) && (isUnselected(operationalIndex) || index < operationalIndex)) ||
        (isSelectedAtCurrentIndex(operationalIndex) && isUnselected(regulatoryIndex)),
      showOperational:
        isSelectedAtCurrentIndex(operationalIndex) ||
        (isUnselected(operationalIndex) && (isUnselected(regulatoryIndex) || index > regulatoryIndex)) ||
        (isSelectedAtCurrentIndex(regulatoryIndex) && isUnselected(operationalIndex)),
    };
  }

  return {
    showRegulatory:
      isSelectedAtCurrentIndex(regulatoryIndex) ||
      (isUnselected(regulatoryIndex) && (isUnselected(operationalIndex) || index > operationalIndex)) ||
      (isSelectedAtCurrentIndex(operationalIndex) && isUnselected(regulatoryIndex)),
    showOperational:
      isSelectedAtCurrentIndex(operationalIndex) ||
      (isUnselected(operationalIndex) && (isUnselected(regulatoryIndex) || index < regulatoryIndex)) ||
      (isSelectedAtCurrentIndex(regulatoryIndex) && isUnselected(operationalIndex)),
  };
};
